import { Color3, CreateBox, PBRMaterial, type Mesh, type Scene, type Vector3 } from "@babylonjs/core";
import { LANDMASSES, clamp, hash2, smoothstep, type IslandTheme, type Landmass } from "@tiny-seas/shared";
import { DAY_SKY_HORIZON, DAY_SKY_ZENITH } from "./atmosphere.ts";

/** Per-theme fog colour, fog density multiplier and sun intensity multiplier at full blend. */
export const ATMOSPHERE = {
  tropical: { fog: DAY_SKY_HORIZON, density: 1, sun: 1 },
  volcanic: { fog: Color3.FromHexString("#8a7a70"), density: 2.2, sun: 0.75 },
  frost: { fog: Color3.FromHexString("#dfe8ee"), density: 2, sun: 0.85 },
  marsh: { fog: Color3.FromHexString("#9fae9a"), density: 3, sun: 0.7 },
  ruins: { fog: Color3.FromHexString("#7f8c99"), density: 1.6, sun: 0.6 },
} as const;
/** Full storm over the themed blend: fog colour, linear sky zenith, and fog density and sun intensity multipliers. */
export const STORM = { fog: Color3.FromHexString("#76838f"), zenith: new Color3(0.12, 0.16, 0.21), density: 1.9, sun: 0.55 } as const;
export type PrecipitationKind = "rain" | "snow" | "ash";
/** Themed precipitation: kind, particle count, particle width and height (m), fall speed (m/s) and colour. */
export const PRECIPITATION = [
  ["ruins", "rain", 1500, 0.03, 0.7, 18, "#b8c8d8"],
  ["frost", "snow", 1200, 0.08, 0.08, 1.5, "#ffffff"],
  ["volcanic", "ash", 800, 0.06, 0.06, 1, "#99928b"],
] as const;
interface Precipitation { theme: IslandTheme; kind: PrecipitationKind; mesh: Mesh; material: PBRMaterial; matrices: Float32Array; offsets: Float32Array; speed: number }
/** Precipitation keeps this far (m) from the camera's vertical axis. */
const CLEAR_RADIUS = 2.5;

/** How strongly `land`'s themed weather reaches (x, z): full within twice its radius, gone 500 m further out. */
export function weatherInfluence(land: Landmass, x: number, z: number): number {
  // Skull's tropical fortress keeps clear maritime light, not the generic ruin rain.
  if (land.theme === "tropical" || land.id === "skull") return 0;
  return 1 - smoothstep(2 * land.radius, 2 * land.radius + 500, Math.hypot(x - land.cx, z - land.cz));
}

/**
 * Visual atmosphere only; deterministic, preallocated precipitation never changes gameplay.
 * Island themes blend in near their islands; the room's storminess (`storm`) darkens the sky
 * and rains everywhere, giving way to an island's own snow or ash.
 */
export class Weather {
  readonly fog = ATMOSPHERE.tropical.fog.clone();
  densityMul = 1;
  sunMul = 1;
  /** Sky zenith (linear), darkened by storms. */
  readonly zenith = DAY_SKY_ZENITH.clone();
  /** 0 fair … 1 full storm (`Environment.setWeather`). */
  storm = 0;
  /** Lab hook: replaces the camera-driven target theme and strength; the blend still eases toward it. */
  override: { theme: IslandTheme; weight: number } | null = null;
  private theme: IslandTheme = "tropical";
  private weight = 0;
  private target: IslandTheme = "tropical";
  private targetStrength = 0;
  private time = 0;
  private readonly precipitation: Precipitation[] = [];

  constructor(scene: Scene) {
    for (const [theme, kind, count, width, height, speed, color] of PRECIPITATION) {
      const mesh = CreateBox(`weather-${theme}`, { width, height, depth: width }, scene);
      const material = new PBRMaterial(`weather-${theme}-material`, scene);
      material.albedoColor = Color3.FromHexString(color);
      material.emissiveColor.copyFrom(material.albedoColor);
      material.unlit = true;
      material.alpha = 0;
      mesh.material = material;
      mesh.isPickable = false;
      mesh.receiveShadows = false;
      mesh.alwaysSelectAsActiveMesh = true;
      const matrices = new Float32Array(count * 16);
      const offsets = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        const m = i * 16;
        matrices[m] = matrices[m + 5] = matrices[m + 10] = matrices[m + 15] = 1;
        let x = hash2(i, 0, 713) * 60 - 30, z = hash2(i, 2, 713) * 60 - 30;
        // Streaks fall in a camera-centred box: one inside the eye's own column would hang in view as a bar.
        const reach = Math.hypot(x, z);
        if (reach < CLEAR_RADIUS) { const s = (CLEAR_RADIUS + reach) / Math.max(reach, 1e-3); x *= s; z *= s; }
        offsets[i * 3] = x;
        offsets[i * 3 + 1] = hash2(i, 1, 713) * 30;
        offsets[i * 3 + 2] = z;
      }
      mesh.thinInstanceSetBuffer("matrix", matrices, 16, false);
      mesh.setEnabled(false);
      this.precipitation.push({ theme, kind, mesh, material, matrices, offsets, speed });
    }
  }

  /** Theme currently blended in. */
  get activeTheme(): IslandTheme {
    return this.theme;
  }

  /** 0–1 blend of `activeTheme` over clear tropical weather. */
  get blend(): number {
    return this.weight;
  }

  /** Theme the blend is easing toward. */
  get targetTheme(): IslandTheme {
    return this.target;
  }

  /** Strength of `targetTheme` the blend is easing toward. */
  get targetWeight(): number {
    return this.targetStrength;
  }

  /** Visible precipitation, or null when none is drawn. */
  get activePrecipitation(): { kind: PrecipitationKind; count: number; alpha: number } | null {
    const p = this.precipitation.find(p => p.mesh.isEnabled());
    return p ? { kind: p.kind, count: p.offsets.length / 3, alpha: p.material.alpha } : null;
  }

  /** Jumps the blend to its target at `camera`, skipping the eased transition. */
  settle(camera: Vector3): void {
    this.aim(camera);
    this.theme = this.target;
    this.weight = this.targetStrength;
  }

  private aim(camera: Vector3): void {
    if (this.override) {
      this.target = this.override.theme;
      this.targetStrength = clamp(this.override.weight, 0, 1);
      return;
    }
    this.target = "tropical";
    this.targetStrength = 0;
    for (const land of LANDMASSES) {
      const weight = weatherInfluence(land, camera.x, camera.z);
      if (weight > this.targetStrength) { this.target = land.theme; this.targetStrength = weight; }
    }
  }

  update(dt: number, camera: Vector3, reducedMotion: boolean, underwater: boolean): void {
    this.time += dt;
    this.aim(camera);
    const goal = this.target === this.theme ? this.targetStrength : 0;
    this.weight += clamp(goal - this.weight, -dt * 0.5, dt * 0.5);
    if (this.weight === 0 && this.target !== this.theme) this.theme = this.target;
    const row = ATMOSPHERE[this.theme];
    Color3.LerpToRef(ATMOSPHERE.tropical.fog, row.fog, this.weight, this.fog);
    Color3.LerpToRef(this.fog, STORM.fog, this.storm, this.fog);
    Color3.LerpToRef(DAY_SKY_ZENITH, STORM.zenith, this.storm, this.zenith);
    this.densityMul = (1 + (row.density - 1) * this.weight) * (1 + (STORM.density - 1) * this.storm);
    this.sunMul = (1 + (row.sun - 1) * this.weight) * (1 + (STORM.sun - 1) * this.storm);
    // An island's own snow or ash replaces storm rain as its blend comes in.
    const ownFall = this.weight > 0 && this.precipitation.some(p => p.theme === this.theme && p.kind !== "rain") ? this.weight : 0;
    for (const p of this.precipitation) {
      const themed = p.theme === this.theme ? this.weight : 0;
      const strength = p.kind === "rain" ? Math.max(themed, this.storm * (1 - ownFall)) : themed;
      const enabled = strength > 0 && !underwater && !reducedMotion;
      p.mesh.setEnabled(enabled);
      if (!enabled) continue;
      p.material.alpha = strength * 0.6;
      const { matrices, offsets } = p;
      const snow = p.kind === "snow";
      for (let i = 0; i < offsets.length / 3; i++) {
        const o = i * 3;
        const m = i * 16;
        const drift = snow ? Math.sin(this.time * 0.8 + i) * 2 : 0;
        matrices[m + 12] = camera.x + ((offsets[o]! + drift + 90) % 60) - 30;
        matrices[m + 13] = camera.y + ((offsets[o + 1]! - this.time * p.speed) % 30 + 30) % 30 - 15;
        matrices[m + 14] = camera.z + offsets[o + 2]!;
      }
      p.mesh.thinInstanceBufferUpdated("matrix");
    }
  }
}
